/**
 * How the board's ground plane is laid out on screen, before the camera. World units are
 * map units (CELL per cell, y south); view units are what the camera pans and zooms.
 * Every projection is affine, so a click inverts exactly: view = M · world.
 *
 * - `flat`: straight down, as before.
 * - `tilted`: seen at an angle from the south, the 2.5D board of the printed game: the grid
 *   keeps its axes (north stays up), depth is squashed, ships and forts stand upright.
 * - `iso`: classic isometric, the grid turned 45° into 2:1 diamonds.
 */
export type ProjectionKind = 'flat' | 'tilted' | 'iso';

export const PROJECTIONS: readonly ProjectionKind[] = ['flat', 'tilted', 'iso'];

/** Depth squash of the tilted view: cos of the viewing angle from vertical. */
export const TILT = 0.7;

export interface Projection {
  readonly kind: ProjectionKind;
  /** view.x = a·x + c·y, view.y = b·x + d·y (Pixi's Matrix layout, no translation). */
  readonly a: number;
  readonly b: number;
  readonly c: number;
  readonly d: number;
  /** Whether units stand up (sprites drawn unprojected over a projected anchor). */
  readonly upright: boolean;
}

const K = Math.SQRT1_2;

export function projection(kind: ProjectionKind): Projection {
  switch (kind) {
    case 'flat':
      return { kind, a: 1, b: 0, c: 0, d: 1, upright: false };
    case 'tilted':
      return { kind, a: 1, b: 0, c: 0, d: TILT, upright: true };
    case 'iso':
      return { kind, a: K, b: K / 2, c: -K, d: K / 2, upright: true };
  }
}

export function isProjectionKind(v: unknown): v is ProjectionKind {
  return v === 'flat' || v === 'tilted' || v === 'iso';
}

export function toView(p: Projection, x: number, y: number): { x: number; y: number } {
  return { x: p.a * x + p.c * y, y: p.b * x + p.d * y };
}

export function toWorld(p: Projection, vx: number, vy: number): { x: number; y: number } {
  const det = p.a * p.d - p.b * p.c;
  return { x: (p.d * vx - p.c * vy) / det, y: (p.a * vy - p.b * vx) / det };
}

/** The view-space box around the projected map rectangle. */
export function viewBounds(
  p: Projection,
  width: number,
  height: number,
): { x: number; y: number; width: number; height: number } {
  const corners = [toView(p, 0, 0), toView(p, width, 0), toView(p, 0, height), toView(p, width, height)];
  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}
